import { Link } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Save,
  Trash2,
  Lightbulb,
  Heart,
  Compass,
  TrendingUp,
  CalendarDays,
  PencilLine,
  Timer,
  Smile,
  Mail,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const REFLECTION_QUESTIONS = [
  {
    id: "accomplished",
    label: "What did I accomplish today?",
    placeholder: "Wins, finished tasks, milestones — even small ones count.",
    icon: CheckCircle2,
    accent: "text-emerald-600 dark:text-emerald-400",
  },
  {
    id: "struggled",
    label: "What did I struggle with or not finish?",
    placeholder: "Be honest. Naming friction is the first step to moving past it.",
    icon: Compass,
    accent: "text-amber-600 dark:text-amber-400",
  },
  {
    id: "learned",
    label: "What did I learn?",
    placeholder: "A lesson, a fact, an insight about yourself or your work.",
    icon: Lightbulb,
    accent: "text-primary",
  },
  {
    id: "grateful",
    label: "What am I grateful for?",
    placeholder: "A person, a small moment, anything that felt good.",
    icon: Heart,
    accent: "text-rose-500",
  },
  {
    id: "next",
    label: "What's my next move?",
    placeholder: "One concrete thing you'll do tomorrow.",
    icon: TrendingUp,
    accent: "text-primary",
  },
];

const EMOTIONS = [
  // positive
  { id: "happy", label: "Happy", emoji: "😊", group: "positive" },
  { id: "grateful", label: "Grateful", emoji: "🙏", group: "positive" },
  { id: "calm", label: "Calm", emoji: "😌", group: "positive" },
  { id: "loved", label: "Loved", emoji: "🥰", group: "positive" },
  { id: "excited", label: "Excited", emoji: "🤩", group: "positive" },
  { id: "proud", label: "Proud", emoji: "🦁", group: "positive" },
  { id: "energetic", label: "Energetic", emoji: "⚡", group: "positive" },
  { id: "creative", label: "Creative", emoji: "🎨", group: "positive" },
  { id: "hopeful", label: "Hopeful", emoji: "🌅", group: "positive" },
  { id: "confident", label: "Confident", emoji: "💪", group: "positive" },
  { id: "playful", label: "Playful", emoji: "🤹", group: "positive" },
  { id: "peaceful", label: "Peaceful", emoji: "🕊️", group: "positive" },
  // neutral
  { id: "focused", label: "Focused", emoji: "🎯", group: "neutral" },
  { id: "curious", label: "Curious", emoji: "🤔", group: "neutral" },
  { id: "tired", label: "Tired", emoji: "😴", group: "neutral" },
  { id: "thoughtful", label: "Thoughtful", emoji: "💭", group: "neutral" },
  { id: "nostalgic", label: "Nostalgic", emoji: "🎞️", group: "neutral" },
  { id: "introspective", label: "Introspective", emoji: "🪞", group: "neutral" },
  // challenging
  { id: "anxious", label: "Anxious", emoji: "😟", group: "challenging" },
  { id: "stressed", label: "Stressed", emoji: "😣", group: "challenging" },
  { id: "sad", label: "Sad", emoji: "😢", group: "challenging" },
  { id: "frustrated", label: "Frustrated", emoji: "😤", group: "challenging" },
  { id: "angry", label: "Angry", emoji: "😠", group: "challenging" },
  { id: "lonely", label: "Lonely", emoji: "🫥", group: "challenging" },
  { id: "overwhelmed", label: "Overwhelmed", emoji: "😵‍💫", group: "challenging" },
  { id: "discouraged", label: "Discouraged", emoji: "😞", group: "challenging" },
  { id: "fearful", label: "Fearful", emoji: "😨", group: "challenging" },
  { id: "bored", label: "Bored", emoji: "🥱", group: "challenging" },
];

const EMOTION_GROUPS = [
  { id: "positive", label: "Uplifting" },
  { id: "neutral", label: "Reflective" },
  { id: "challenging", label: "Challenging" },
];

function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtDateLabel(key) {
  const [Y, M, D] = key.split("-").map(Number);
  return new Date(Y, M - 1, D).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function LogPage() {
  const [date, setDate] = useState(todayKey());
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [timeExpected, setTimeExpected] = useState("");
  const [timeActual, setTimeActual] = useState("");
  const [emotions, setEmotions] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const loadEntry = useCallback(async (d) => {
    const { data } = await api.get(`/logs/by-date/${d}`);
    setAnswers((data && data.answers) || {});
    setNotes((data && data.notes) || "");
    setTimeExpected(
      data && data.time_expected_minutes != null ? String(data.time_expected_minutes) : ""
    );
    setTimeActual(
      data && data.time_actual_minutes != null ? String(data.time_actual_minutes) : ""
    );
    setEmotions((data && data.emotions) || []);
  }, []);

  const loadHistory = useCallback(async () => {
    const { data } = await api.get("/logs", { params: { limit: 60 } });
    setHistory(data);
  }, []);

  useEffect(() => {
    loadEntry(date);
  }, [loadEntry, date]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/logs", {
        date,
        notes,
        answers,
        time_expected_minutes: timeExpected === "" ? null : parseInt(timeExpected, 10),
        time_actual_minutes: timeActual === "" ? null : parseInt(timeActual, 10),
        emotions,
      });
      setSavedAt(new Date());
      toast.success("Reflection saved");
      loadHistory();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/logs/${id}`);
    loadHistory();
    if (date === todayKey()) loadEntry(date);
    toast.success("Entry deleted");
  };

  const stats = useMemo(() => {
    const filled = Object.values(answers).filter((a) => (a || "").trim().length > 0).length;
    return {
      filled,
      total: REFLECTION_QUESTIONS.length,
      hasNotes: notes.trim().length > 0,
    };
  }, [answers, notes]);

  const setAnswer = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));

  const toggleEmotion = (id) => {
    setEmotions((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const expectedNum = timeExpected === "" ? null : parseInt(timeExpected, 10);
  const actualNum = timeActual === "" ? null : parseInt(timeActual, 10);
  const timeDelta =
    expectedNum != null && actualNum != null && !isNaN(expectedNum) && !isNaN(actualNum)
      ? actualNum - expectedNum
      : null;

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <MenuSheet />
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="rounded-xl"
            data-testid="log-back-button"
          >
            <Link to="/">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to planner
            </Link>
          </Button>
        </div>

        {/* Title */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <BookOpen strokeWidth={1.5} className="size-3.5" />
            Personal log
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Reflect & record
          </h1>
          <p className="text-base text-muted-foreground">
            Slow down for a moment. Answer the prompts that resonate, then jot any free thoughts below.
          </p>
        </div>

        {/* Letters to future self link */}
        <Link
          to="/letters"
          data-testid="letters-shortcut"
          className="block rounded-2xl border-2 border-primary/30 bg-card p-5 hover:border-primary/60 hover:-translate-y-[1px] transition-all shadow-sm"
          style={{
            backgroundImage:
              "radial-gradient(circle at 100% 0%, hsl(var(--primary) / 0.10), transparent 50%)",
          }}
        >
          <div className="flex items-center gap-4">
            <div className="size-11 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
              <Mail strokeWidth={1.5} className="size-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Reflection · sub-section
              </div>
              <div className="font-display text-lg font-bold tracking-tight">
                Letters to future self
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Write to the version of you 30, 60, or 90 days ahead. Sealed until then.
              </div>
            </div>
            <ArrowRight strokeWidth={1.5} className="size-5 text-muted-foreground shrink-0" />
          </div>
        </Link>

        {/* Date + status */}
        <Card className="rounded-2xl">
          <CardContent className="pt-6 flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
            <div className="flex-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Entry date
              </Label>
              <div className="flex items-center gap-3 mt-1.5">
                <CalendarDays strokeWidth={1.5} className="size-5 text-primary" />
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  max={todayKey()}
                  className="h-11 max-w-[200px]"
                  data-testid="log-date-input"
                />
                <span className="text-sm text-muted-foreground hidden sm:inline">
                  {fmtDateLabel(date)}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Prompts answered
              </div>
              <div className="font-display text-2xl font-extrabold">
                {stats.filled}/{stats.total}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reflection questions */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">Reflection prompts</CardTitle>
            <CardDescription>
              Each prompt is optional. They help you spot patterns over time.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {REFLECTION_QUESTIONS.map((q) => {
              const Icon = q.icon;
              const value = answers[q.id] || "";
              const filled = value.trim().length > 0;
              return (
                <div key={q.id} className="space-y-2" data-testid={`log-question-${q.id}`}>
                  <div className="flex items-center gap-2">
                    <Icon strokeWidth={1.5} className={`size-4 ${q.accent}`} />
                    <Label
                      htmlFor={`q-${q.id}`}
                      className="text-sm font-semibold flex-1"
                    >
                      {q.label}
                    </Label>
                    {filled && (
                      <CheckCircle2 strokeWidth={2} className="size-4 text-emerald-500" />
                    )}
                  </div>
                  <Textarea
                    id={`q-${q.id}`}
                    rows={3}
                    value={value}
                    placeholder={q.placeholder}
                    onChange={(e) => setAnswer(q.id, e.target.value)}
                    data-testid={`log-answer-${q.id}`}
                    className="resize-y"
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Time estimation */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <Timer strokeWidth={1.5} className="size-5 text-primary" />
              Time check
            </CardTitle>
            <CardDescription>
              Compare how long you thought something would take versus how long it actually did. Helps you plan better over time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="time-expected" className="text-sm font-semibold">
                  How long do you think this will take?
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="time-expected"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g. 60"
                    value={timeExpected}
                    onChange={(e) => setTimeExpected(e.target.value)}
                    className="h-11"
                    data-testid="log-time-expected"
                  />
                  <span className="text-sm text-muted-foreground">minutes</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="time-actual" className="text-sm font-semibold">
                  How long did this actually take?
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="time-actual"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g. 95"
                    value={timeActual}
                    onChange={(e) => setTimeActual(e.target.value)}
                    className="h-11"
                    data-testid="log-time-actual"
                  />
                  <span className="text-sm text-muted-foreground">minutes</span>
                </div>
              </div>
            </div>
            {timeDelta !== null && (
              <div
                className={`mt-4 rounded-xl border p-3 text-sm ${
                  timeDelta > 0
                    ? "border-amber-400/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                    : timeDelta < 0
                    ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "border-border bg-secondary/50"
                }`}
                data-testid="log-time-delta"
              >
                {timeDelta === 0 ? (
                  <>Right on the dot — nice estimation.</>
                ) : timeDelta > 0 ? (
                  <>
                    Took <strong>{timeDelta} min</strong> longer than expected. Next time, add a buffer.
                  </>
                ) : (
                  <>
                    Finished <strong>{Math.abs(timeDelta)} min</strong> faster than planned. Nice work.
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Emotions */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <Smile strokeWidth={1.5} className="size-5 text-primary" />
              How did you feel today?
            </CardTitle>
            <CardDescription>
              Tap any feelings that fit. Logging emotions helps you spot patterns over time.
              {emotions.length > 0 && (
                <>
                  {" "}
                  <span className="font-semibold text-foreground">
                    {emotions.length} selected
                  </span>
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {EMOTION_GROUPS.map((g) => (
              <div key={g.id}>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  {g.label}
                </div>
                <div className="flex flex-wrap gap-2">
                  {EMOTIONS.filter((e) => e.group === g.id).map((e) => {
                    const active = emotions.includes(e.id);
                    return (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => toggleEmotion(e.id)}
                        data-testid={`log-emotion-${e.id}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-sm transition-all ${
                          active
                            ? "border-primary bg-primary/10 text-foreground font-semibold"
                            : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                        }`}
                      >
                        <span className="text-base leading-none">{e.emoji}</span>
                        {e.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Free notes */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <PencilLine strokeWidth={1.5} className="size-5 text-primary" />
              Free notes
            </CardTitle>
            <CardDescription>
              Anything else on your mind — ideas, feelings, scraps of writing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              rows={6}
              value={notes}
              placeholder="Write freely here…"
              onChange={(e) => setNotes(e.target.value)}
              data-testid="log-notes-input"
              className="resize-y"
            />
          </CardContent>
        </Card>

        {/* Save bar */}
        <div className="sticky bottom-4 z-10">
          <div className="rounded-2xl border-2 border-border bg-card/95 backdrop-blur p-3 sm:p-4 flex items-center justify-between gap-3 shadow-md">
            <div className="text-xs text-muted-foreground min-w-0">
              {savedAt ? (
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 strokeWidth={2} className="size-3.5 text-emerald-500" />
                  Saved at {savedAt.toLocaleTimeString()}
                </span>
              ) : (
                <span>Save when you're done. Auto-loads if you change the date.</span>
              )}
            </div>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-xl"
              data-testid="log-save-button"
            >
              <Save strokeWidth={1.5} className="size-4 mr-1.5" />
              {saving ? "Saving…" : "Save reflection"}
            </Button>
          </div>
        </div>

        {/* History */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">History</CardTitle>
            <CardDescription>
              {history.length === 0
                ? "Your past reflections will collect here."
                : `${history.length} past ${history.length === 1 ? "entry" : "entries"}.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground py-3">Nothing yet.</p>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {history.map((entry) => {
                  const answersFilled = Object.values(entry.answers || {}).filter(
                    (a) => (a || "").trim()
                  ).length;
                  return (
                    <AccordionItem
                      value={entry.id}
                      key={entry.id}
                      data-testid={`log-history-${entry.id}`}
                    >
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3 flex-1 text-left">
                          <span className="font-semibold">{fmtDateLabel(entry.date)}</span>
                          <span className="text-xs text-muted-foreground">
                            {answersFilled} prompt{answersFilled === 1 ? "" : "s"} answered
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 pt-1">
                          {REFLECTION_QUESTIONS.map((q) => {
                            const a = (entry.answers || {})[q.id];
                            if (!a || !a.trim()) return null;
                            return (
                              <div key={q.id} className="rounded-xl bg-secondary/50 p-3">
                                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                                  {q.label}
                                </div>
                                <div className="text-sm whitespace-pre-wrap">{a}</div>
                              </div>
                            );
                          })}
                          {(entry.time_expected_minutes != null || entry.time_actual_minutes != null) && (
                            <div className="rounded-xl bg-secondary/50 p-3">
                              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                                Time check
                              </div>
                              <div className="text-sm">
                                {entry.time_expected_minutes != null && (
                                  <>Expected {entry.time_expected_minutes}m</>
                                )}
                                {entry.time_expected_minutes != null && entry.time_actual_minutes != null && " · "}
                                {entry.time_actual_minutes != null && (
                                  <>Actual {entry.time_actual_minutes}m</>
                                )}
                              </div>
                            </div>
                          )}
                          {entry.emotions && entry.emotions.length > 0 && (
                            <div className="rounded-xl bg-secondary/50 p-3">
                              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                                Emotions
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {entry.emotions.map((eid) => {
                                  const em = EMOTIONS.find((x) => x.id === eid);
                                  if (!em) return null;
                                  return (
                                    <span
                                      key={eid}
                                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-border bg-card"
                                    >
                                      <span>{em.emoji}</span>
                                      {em.label}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {entry.notes && entry.notes.trim() && (
                            <div className="rounded-xl bg-secondary/50 p-3">
                              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                                Notes
                              </div>
                              <div className="text-sm whitespace-pre-wrap">{entry.notes}</div>
                            </div>
                          )}
                          <div className="flex justify-end pt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDate(entry.date)}
                              className="rounded-xl"
                              data-testid={`log-history-load-${entry.id}`}
                            >
                              Edit this entry
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => remove(entry.id)}
                              className="rounded-xl text-destructive hover:text-destructive"
                              data-testid={`log-history-delete-${entry.id}`}
                            >
                              <Trash2 strokeWidth={1.5} className="size-4 mr-1.5" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
